import { Injectable } from '@angular/core'
import { Router } from '@angular/router'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { of } from 'rxjs'
import { catchError, map, switchMap, tap } from 'rxjs/operators'
import {
  CreateCompoundFailure,
  CreateCompoundPaymentFailure,
  CreateCompoundPaymentRequest,
  CreateCompoundPaymentSuccess,
  CreateCompoundRequest,
  CreateCompoundSuccess,
  DeleteCompoundDocumentFailure,
  DeleteCompoundDocumentRequest,
  DeleteCompoundDocumentSuccess,
  DeleteCompoundPaymentFailure,
  DeleteCompoundPaymentRequest,
  DeleteCompoundPaymentSuccess,
  DownloadCompoundDocumentFailure,
  DownloadCompoundDocumentRequest,
  DownloadCompoundDocumentSuccess,
  DownloadCompoundPaymentFailure,
  DownloadCompoundPaymentRequest,
  DownloadCompoundPaymentSuccess,
  FetchCompoundDocumentsFailure,
  FetchCompoundDocumentsRequest,
  FetchCompoundDocumentsSuccess,
  FetchCompoundFailure,
  FetchCompoundPaymentsFailure,
  FetchCompoundPaymentsRequest,
  FetchCompoundPaymentsSuccess,
  FetchCompoundRequest,
  FetchCompoundSuccess,
  FetchCompoundsFailure,
  FetchCompoundsRequest,
  FetchCompoundsSuccess,
  GenerateCompoundDocumentFailure,
  GenerateCompoundDocumentRequest,
  GenerateCompoundDocumentSuccess,
  UpdateCompoundFailure,
  UpdateCompoundPaymentFailure,
  UpdateCompoundPaymentRequest,
  UpdateCompoundPaymentSuccess,
  UpdateCompoundRequest,
  UpdateCompoundSuccess,
} from './compounds.actions'
import { CompoundsService } from './compounds.service'
import * as CompoundsTypes from './compounds.types'
import { downloadBlob } from '@shared/shared.utils'
import {
  DownloadPropertyDocumentFailure,
  DownloadPropertyDocumentSuccess,
} from '@containers/properties/properties.actions'

@Injectable()
export class CompoundsEffects {
  constructor(
    private actions: Actions,
    private compoundsService: CompoundsService,
    private router: Router,
  ) {}

  FetchBusinessPayments$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.FETCH_COMPOUND_PAYMENTS_REQUEST),
      switchMap((action: FetchCompoundPaymentsRequest) => {
        return this.compoundsService.fetchCompoundPayments(action.id).pipe(
          map((result) => {
            return new FetchCompoundPaymentsSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchCompoundPaymentsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  CreatePayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.CREATE_COMPOUND_PAYMENT_REQUEST),
      switchMap((action: CreateCompoundPaymentRequest) => {
        return this.compoundsService.createCompoundPayment(action.payload).pipe(
          switchMap((result) => {
            return [new CreateCompoundPaymentSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new CreateCompoundPaymentFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  UpdatePayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.UPDATE_COMPOUND_PAYMENT_REQUEST),
      switchMap((action: UpdateCompoundPaymentRequest) => {
        return this.compoundsService.updateCompoundPayment(action.payload).pipe(
          switchMap((result) => {
            return [new UpdateCompoundPaymentSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new UpdateCompoundPaymentFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DeletePayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.DELETE_COMPOUND_PAYMENT_REQUEST),
      switchMap((action: DeleteCompoundPaymentRequest) => {
        return this.compoundsService.deleteCompoundPayment(action.payload).pipe(
          switchMap((result) => {
            return [new DeleteCompoundPaymentSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new DeleteCompoundPaymentFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DownloadPayment$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.DOWNLOAD_COMPOUND_PAYMENT_REQUEST),
      switchMap((action: DownloadCompoundPaymentRequest) => {
        return this.compoundsService
          .downloadCompoundPayment(action.payload)
          .pipe(
            switchMap((result) => {
              return [new DownloadCompoundPaymentSuccess(result)]
            }),
            catchError((err, caught) => {
              return of(new DownloadCompoundPaymentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  SavePaymentReceipt$ = createEffect(
    () =>
      this.actions.pipe(
        ofType<DownloadCompoundPaymentSuccess>(
          CompoundsTypes.DOWNLOAD_COMPOUND_PAYMENT_SUCCESS,
        ),
        tap((action) => downloadBlob(action.payload)),
      ),
    { dispatch: false },
  )

  FetchCompounds$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.FETCH_COMPOUNDS_REQUEST),
      switchMap((action: FetchCompoundsRequest) => {
        return this.compoundsService.fetchCompounds(action.payload).pipe(
          map((result) => {
            return new FetchCompoundsSuccess(
              result.data.results,
              result.data.filteredCount,
            )
          }),
          catchError((err, caught) => {
            return of(new FetchCompoundsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  UpdateCompound$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.UPDATE_COMPOUND_REQUEST),
      switchMap((action: UpdateCompoundRequest) => {
        return this.compoundsService
          .updateCompound(action.id, action.payload)
          .pipe(
            switchMap((result) => {
              return [new UpdateCompoundSuccess(result.data)]
            }),
            catchError((err, caught) => {
              return of(new UpdateCompoundFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  CreateCompound$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.CREATE_COMPOUND_REQUEST),
      switchMap((action: CreateCompoundRequest) => {
        return this.compoundsService.createCompound(action.payload).pipe(
          switchMap((result) => {
            this.router.navigate(['/compounds'])

            return [new CreateCompoundSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new CreateCompoundFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchCompound$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.FETCH_COMPOUND_REQUEST),
      switchMap((action: FetchCompoundRequest) => {
        return this.compoundsService.fetchCompound(action.id).pipe(
          switchMap((result) => {
            return [new FetchCompoundSuccess(result.data)]
          }),
          catchError((err, caught) => {
            return of(new FetchCompoundFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  FetchCompoundDocuments$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.FETCH_COMPOUND_DOCUMENTS_REQUEST),
      switchMap((action: FetchCompoundDocumentsRequest) => {
        return this.compoundsService.fetchCompoundDocuments(action.id).pipe(
          map((result) => {
            return new FetchCompoundDocumentsSuccess(result.data)
          }),
          catchError((err, caught) => {
            return of(new FetchCompoundDocumentsFailure({ error: err }))
          }),
        )
      }),
    ),
  )

  DownloadCompoundDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.DOWNLOAD_COMPOUND_DOCUMENT_REQUEST),
      switchMap((action: DownloadCompoundDocumentRequest) => {
        return this.compoundsService
          .downloadCompoundDocument(action.payload)
          .pipe(
            switchMap((result) => {
              return [new DownloadCompoundDocumentSuccess(result, null)]
            }),
            catchError((err, caught) => {
              return of(new DownloadCompoundDocumentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  SaveCompoundDocument$ = createEffect(
    () =>
      this.actions.pipe(
        ofType<DownloadCompoundDocumentSuccess>(
          CompoundsTypes.DOWNLOAD_COMPOUND_DOCUMENT_SUCCESS,
        ),
        tap((action) => downloadBlob(action.payload)),
      ),
    { dispatch: false },
  )

  GenerateCompoundDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.GENERATE_COMPOUND_DOCUMENT_REQUEST),
      switchMap((action: GenerateCompoundDocumentRequest) => {
        return this.compoundsService
          .generateCompoundDocument(action.payload)
          .pipe(
            switchMap((result) => {
              return [new GenerateCompoundDocumentSuccess(result, null)]
            }),
            catchError((err, caught) => {
              return of(new GenerateCompoundDocumentFailure({ error: err }))
            }),
          )
      }),
    ),
  )

  DeleteCompoundDocument$ = createEffect(() =>
    this.actions.pipe(
      ofType(CompoundsTypes.DELETE_COMPOUND_DOCUMENT_REQUEST),
      switchMap((action: DeleteCompoundDocumentRequest) => {
        return this.compoundsService
          .deleteCompoundDocument(action.payload)
          .pipe(
            switchMap((result) => {
              return [
                new DeleteCompoundDocumentSuccess(result),
                new FetchCompoundDocumentsRequest(action.payload.compoundId),
              ]
            }),
            catchError((err, caught) => {
              return of(new DeleteCompoundDocumentFailure({ error: err }))
            }),
          )
      }),
    ),
  )
}
